import { createDinero } from '@mr-yum/frontend-core/dist/services/createDinero'
import { RouteLink } from '@mr-yum/frontend-core/dist/services/routes'
import { CenterSpinner, ExternalLinkOutlineIcon } from '@mr-yum/frontend-ui'
import { format, parseISO } from 'date-fns'
import React, { Fragment } from 'react'
import { FormattedMessage } from 'react-intl'
import ReactMarkdown from 'react-markdown'
import { CombinedError } from 'urql'

import { NotFound } from '@/components/Common/NotFound'
import { ALLOWED_COMMON_ELEMENTS } from '@/components/Shared/Markdown'
import { OrderingType } from '@/gql/graphql'
import { reportError } from '@/lib/bugsnag'
import { taxIdNumberLabels } from '@/lib/gstNumberUtils'
import { routes } from '@/lib/routes'
import { taxInvoiceLabels } from '@/lib/taxInvoiceUtils'

import { OrderItems } from './OrderItems'
import {
  CURRENCIES,
  dateFormat,
  dateOnlyFormat,
  displayCompanyList,
  InvoiceLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
  TableWithoutBorder,
} from './shared'
import { InvoiceProps } from './types'

interface Props extends InvoiceProps {
  fetching?: boolean
  error?: CombinedError | null
}

export const Invoice = ({
  fetching,
  error,
  data,
  options = {
    showInvoiceId: false,
    showLinksToChildVenueInvoices: false,
    showNotes: true,
  },
}: Props) => {
  if (fetching) {
    return <CenterSpinner />
  }

  if (error) {
    reportError(error)
    return <NotFound className="!w-auto" />
  }

  if (!data || !data.lineItems || !data.lineItems.length) {
    return <NotFound className="!w-auto" />
  }

  const {
    invoice,
    bill,
    venue,
    guest,
    lineItems,
    flatAdditionalLineItems,
    refunds,
  } = data
  const currency = bill.currency as keyof typeof CURRENCIES
  const displayCompany = displayCompanyList[currency] || displayCompanyList.AUD

  return (
    <div className="mx-auto max-w-[500px] text-foreground my-body-md">
      <Table>
        <TableHead className="mb-1">
          <TableHeadCell>
            {taxInvoiceLabels[currency] || (
              <FormattedMessage
                defaultMessage="Tax invoice & receipt"
                id="6K9Cjw"
              />
            )}
          </TableHeadCell>
        </TableHead>

        <TableBody>
          <TableRow>
            <TableCell>
              <FormattedMessage defaultMessage="Venue" id="PYmHhr" />
            </TableCell>

            <TableCell className="flex-1 text-right">
              <InvoiceLink href={`https://www.meandu.app/${venue.slug}`}>
                {venue.name}
              </InvoiceLink>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>
              <FormattedMessage defaultMessage="Order date" id="nYDnup" />
            </TableCell>

            <TableCell className="flex-1 text-right">
              {format(parseISO(invoice.createdAt), dateFormat)}
            </TableCell>
          </TableRow>

          {invoice.tableNumber && (
            <TableRow>
              <TableCell>
                <FormattedMessage defaultMessage="Table number" id="SCjZeO" />
              </TableCell>

              <TableCell className="flex-1 text-right">
                {invoice.tableNumber}
              </TableCell>
            </TableRow>
          )}

          {options.showInvoiceId && (
            <TableRow>
              <TableCell>
                <FormattedMessage defaultMessage="Invoice" id="Ecfndm" />
              </TableCell>

              <TableCell className="flex-1 text-right">{invoice.id}</TableCell>
            </TableRow>
          )}

          {invoice.batchPosDisplayIds &&
            invoice.batchPosDisplayIds.map((batchPosDisplayId, index) => (
              <TableRow key={index}>
                <TableCell>
                  <FormattedMessage
                    defaultMessage="POS Invoice ID #{number}"
                    id="usYgs8"
                    values={{
                      number: index + 1,
                    }}
                  />
                </TableCell>

                <TableCell className="flex-1 text-right">
                  {batchPosDisplayId}
                </TableCell>
              </TableRow>
            ))}

          {venue.gstRegistrationNumber && (
            <TableRow>
              <TableCell>
                {taxIdNumberLabels[currency] || (
                  <FormattedMessage
                    defaultMessage="Tax identification number"
                    id="8NphIy"
                  />
                )}
              </TableCell>

              <TableCell className="flex-1 text-right">
                {venue.gstRegistrationNumber}
              </TableCell>
            </TableRow>
          )}

          <TableRow>
            <TableCell isBold>
              <FormattedMessage defaultMessage="Total paid" id="F8k2je" />
            </TableCell>

            <TableCell className="flex-1 text-right" isBold>
              {createDinero(
                invoice.paidTotalIncludingTaxInCents,
                currency,
              ).toFormat()}
            </TableCell>
          </TableRow>

          {!!refunds && !!refunds.length && (
            <TableRow>
              <TableCell isBold>
                <FormattedMessage defaultMessage="Total refunds" id="wjcFCJ" />
              </TableCell>

              <TableCell className="flex-1 text-right" isBold>
                {createDinero(
                  invoice.refundedTotalIncludingTaxInCents,
                  currency,
                ).toFormat()}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {(guest ||
        venue.additionalInvoiceInformation ||
        invoice.orderingWindowStartDate) && (
        <Table>
          {guest && (
            <TableHead className="mb-1">
              <TableHeadCell>
                {guest?.name ?? (
                  <FormattedMessage defaultMessage="Your details" id="HmykEw" />
                )}
              </TableHeadCell>
            </TableHead>
          )}
          <div>
            {guest?.email && (
              <TableCell>
                <FormattedMessage defaultMessage="Email:" id="xpTPb3" />{' '}
                {guest.email}
              </TableCell>
            )}

            {guest?.mobile && (
              <TableCell>
                <FormattedMessage defaultMessage="Phone:" id="r6JIKW" />{' '}
                {guest.mobile}
              </TableCell>
            )}

            {venue.additionalInvoiceInformation && (
              <div>
                <ReactMarkdown
                  unwrapDisallowed
                  allowedElements={ALLOWED_COMMON_ELEMENTS}
                  components={{
                    a: ({ href, children }) => (
                      <InvoiceLink target="_blank" href={href}>
                        {children}
                      </InvoiceLink>
                    ),
                  }}
                >
                  {venue.additionalInvoiceInformation}
                </ReactMarkdown>
              </div>
            )}

            {invoice.orderingWindowStartDate && (
              <TableCell>
                <FormattedMessage
                  defaultMessage="Ordering window:"
                  id="heD6RA"
                />{' '}
                {format(parseISO(invoice.orderingWindowStartDate), dateFormat)}
              </TableCell>
            )}
          </div>
        </Table>
      )}

      <div data-testid="invoice-line-items">
        {lineItems.map((group, index) => (
          <Table
            key={index}
            data-testid={
              group.groupTitle?.replace(/^[a-z]/i, '_') ?? 'ungrouped'
            }
          >
            <TableHead>
              <TableHeadCell className="mb-3">
                {group.groupTitle ?? (
                  <FormattedMessage defaultMessage="Your order" id="yLi5uF" />
                )}
              </TableHeadCell>
            </TableHead>

            <TableBody>
              <OrderItems lineItems={group.menuItems} currency={currency} />
              {group.isMvo && options.showLinksToChildVenueInvoices && (
                <TableCell className="mt-2 text-left">
                  <RouteLink
                    route={routes.bill}
                    pathData={{
                      venueSlug: group.groupSlug!,
                      billId: bill.id,
                      cid: group.collectionId,
                      rid: group.referenceId,
                    }}
                  >
                    <a aria-label="View receipt">
                      View receipt{' '}
                      <ExternalLinkOutlineIcon
                        width={18}
                        height={18}
                        className="inline-block align-sub"
                      />
                    </a>
                  </RouteLink>
                </TableCell>
              )}
            </TableBody>
          </Table>
        ))}
      </div>

      <Table data-testid="invoice-totals">
        <TableBody>
          <OrderItems lineItems={flatAdditionalLineItems} currency={currency} />

          <TableRow
            className={
              flatAdditionalLineItems.length ? 'mt-4 border-t pt-4' : ''
            }
          >
            <TableCell className="flex-1">
              <FormattedMessage id="L8seEc" defaultMessage="Subtotal" />
            </TableCell>

            <TableCell className="text-right" data-testid="invoice-subtotal">
              {createDinero(
                invoice.paidTotalIncludingTaxInCents -
                  invoice.paidTotalTaxInCents,
                currency,
              ).toFormat()}
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell className="flex-1">{displayCompany.taxName}</TableCell>

            <TableCell className="text-right" data-testid="invoice-total-tax">
              {createDinero(invoice.paidTotalTaxInCents, currency).toFormat()}
            </TableCell>
          </TableRow>

          <TableRow className="mt-3 my-label-md">
            <TableCell className="flex-1" isBold>
              <FormattedMessage defaultMessage="Total paid" id="F8k2je" />
            </TableCell>

            <TableCell
              className="text-right"
              data-testid="invoice-total"
              isBold
            >
              {createDinero(
                invoice.paidTotalIncludingTaxInCents,
                currency,
              ).toFormat()}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      {!!refunds && !!refunds.length && (
        <div data-testid="invoice-refunds-with-totals">
          <Table data-testid="invoice-refunds">
            <TableHead>
              <TableHeadCell>
                <FormattedMessage defaultMessage="Refunds" id="pXQSzm" />
              </TableHeadCell>
            </TableHead>

            <TableBody>
              {refunds.map((group, index) => (
                <Fragment key={index}>
                  <TableRow
                    className={
                      !index
                        ? 'mb-2 mt-2 my-heading-xs'
                        : 'mb-2 mt-3 my-heading-xs'
                    }
                  >
                    <FormattedMessage
                      defaultMessage="Refunded on"
                      id="BLIsyL"
                    />{' '}
                    {format(parseISO(group.date), dateOnlyFormat)}
                  </TableRow>

                  <OrderItems lineItems={group.lineItems} currency={currency} />
                </Fragment>
              ))}

              <div className="mt-4 my-label-md">
                {!venue.isTaxInclusive && (
                  <TableRow>
                    <TableCell className="flex-1">
                      <FormattedMessage defaultMessage="Refunded" id="Gs86nL" />{' '}
                      {displayCompany.taxName}
                    </TableCell>

                    <TableCell
                      className="text-right"
                      data-testid="invoice-refund-tax"
                    >
                      {createDinero(
                        invoice.refundedTotalTaxInCents,
                        currency,
                      ).toFormat()}
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell className="flex-1" isBold>
                    <FormattedMessage
                      defaultMessage="Total refunds"
                      id="wjcFCJ"
                    />
                  </TableCell>

                  <TableCell
                    className="text-right"
                    data-testid="invoice-refund-total"
                    isBold
                  >
                    {createDinero(
                      invoice.refundedTotalIncludingTaxInCents,
                      currency,
                    ).toFormat()}
                  </TableCell>
                </TableRow>
              </div>
            </TableBody>
          </Table>

          <TableWithoutBorder data-testid="invoice-net-totals">
            <TableBody>
              <TableRow>
                <TableCell className="flex-1" isBold>
                  <FormattedMessage
                    defaultMessage="Paid less refunds"
                    id="vCFI2K"
                  />
                </TableCell>

                <TableCell
                  className="text-right"
                  data-testid="invoice-net-total"
                  isBold
                >
                  {createDinero(
                    invoice.netTotalIncludingTaxInCents,
                    currency,
                  ).toFormat()}
                </TableCell>
              </TableRow>

              <TableRow className="mt-3 my-label-md">
                <TableCell className="flex-1">
                  <FormattedMessage defaultMessage="Net subtotal" id="+iJt7n" />
                </TableCell>

                <TableCell
                  className="text-right"
                  data-testid="invoice-net-subtotal"
                >
                  {createDinero(
                    invoice.netTotalIncludingTaxInCents -
                      invoice.netTotalTaxInCents,
                    currency,
                  ).toFormat()}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell className="flex-1">
                  <FormattedMessage defaultMessage="Net" id="nwHYMV" />{' '}
                  {displayCompany.taxName}
                </TableCell>

                <TableCell
                  className="text-right"
                  data-testid="invoice-net-total-tax"
                >
                  {createDinero(
                    invoice.netTotalTaxInCents,
                    currency,
                  ).toFormat()}
                </TableCell>
              </TableRow>
            </TableBody>
          </TableWithoutBorder>
        </div>
      )}

      {options.showNotes &&
        (invoice.deliveryOption ||
          invoice.pickupOption ||
          invoice.orderNotes) && (
          <Table>
            <TableHead>
              <TableHeadCell>
                <FormattedMessage defaultMessage="Notes" id="7+Domh" />
              </TableHeadCell>
            </TableHead>

            <div className="text-foreground">
              {invoice.orderingType &&
                [OrderingType.PickUp, OrderingType.Counter].includes(
                  invoice.orderingType,
                ) &&
                invoice.pickupOption && (
                  <TableCell>
                    <FormattedMessage
                      defaultMessage="Pick-up option:"
                      id="CdcCNf"
                    />{' '}
                    {invoice.pickupOption}
                  </TableCell>
                )}

              {invoice.orderingType === OrderingType.Delivery &&
                invoice.deliveryOption && (
                  <TableCell>
                    <FormattedMessage
                      defaultMessage="Delivery option:"
                      id="VWvhBw"
                    />{' '}
                    {invoice.deliveryOption}
                  </TableCell>
                )}

              {invoice.orderNotes && (
                <TableCell>
                  <FormattedMessage defaultMessage="Order notes:" id="0qZozk" />{' '}
                  {invoice.orderNotes}
                </TableCell>
              )}
            </div>
          </Table>
        )}

      <div className="mb-4 w-full text-center text-foreground my-body-xs">
        <FormattedMessage
          description="Invoice last updated label"
          defaultMessage="Last Updated:"
          id="6ebOT2"
        />{' '}
        {format(parseISO(bill.updatedAt), dateFormat)}
      </div>

      <div className="mb-6 bg-surface p-4 px-4 text-center text-foreground my-body-xs">
        <div className="my-body-sm-bold">
          <FormattedMessage
            description="Company email label"
            defaultMessage="Have questions about your order?"
            id="hT+RxR"
          />
        </div>

        {venue.supportPhone && (
          <div className="py-2">
            <FormattedMessage
              description="Venue support phone label"
              defaultMessage="Call {venueName} on"
              values={{ venueName: venue.name }}
              id="qJ3Fg5"
            />{' '}
            <InvoiceLink href={`tel:${venue.supportPhone}`}>
              {venue.supportPhone}
            </InvoiceLink>
          </div>
        )}

        <div>
          <FormattedMessage defaultMessage="Or you can" id="0avUG2" />{' '}
          <InvoiceLink
            href={`mailto:${venue.supportEmail ?? 'hello@meandu.com'}`}
            className="underline"
          >
            <FormattedMessage defaultMessage="reach out by email" id="68NGuT" />
          </InvoiceLink>
        </div>
      </div>

      <div className="w-full text-center text-foreground my-body-xs">
        <div>{displayCompany.name}</div>

        {!venue.gstRegistrationNumber && currency === 'AUD' && (
          <div>{displayCompany.regNum}</div>
        )}

        <div>
          <FormattedMessage
            description="Company email label"
            defaultMessage="Email:"
            id="T44B7k"
          />{' '}
          <InvoiceLink href={`mailto: ${displayCompany.email}`}>
            {displayCompany.email}
          </InvoiceLink>
        </div>
      </div>
    </div>
  )
}
